import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import type { IconId } from '@tmh-website/components/icons/phosphor-extension';
import type { IconSvgObject, LinkField, NavigationHighlightLinkGroup } from '@tmh-website/shared/cms';
import type { LocalePrefix } from '@tmh-website/shared/const';
import HorizontalScrollContainer from '@tmh-website/components/shared/HorizontalScrollContainer';
import StyledGlobal from '@tmh-website/styles/global';
import { isValidLinkField } from '@tmh-website/shared/cms';
import { ScrollContext } from '@tmh-website/shared';

import type { HeaderThemeColor } from '../Header.theme';
import { headerThemes } from '../Header.theme';
import NavigationHighlightLink from '../NavigationHighlightLink';
import NavigationHighlightLinkStandalone from '../NavigationHighlightLinkStandalone';

import * as Styled from './SubNavigations.styles';

interface SubNavigationProps {
  headerTheme: HeaderThemeColor;
  hideSubnav: () => void;
  isStandaloneMode: boolean;
  locale: LocalePrefix;
  showSubnav: (id: number) => void;

  /**
   * The highlight navigation links to display
   */
  visibleHighlightLinkGroups: NavigationHighlightLinkGroup[];

  visibleSubnav?: number;
}

const SubNavigations = ({
  headerTheme,
  hideSubnav,
  isStandaloneMode,
  locale,
  showSubnav,
  visibleHighlightLinkGroups,
  visibleSubnav,
}: SubNavigationProps) => {
  const { t } = useTranslation();
  const { headerIsVisible, localeSwitchBannerIsVisible } = useContext(ScrollContext);
  if (visibleHighlightLinkGroups.length === 0) {
    return null;
  }

  return (
    <>
      {visibleHighlightLinkGroups.map(group => {
        if (!group.children) {
          return null;
        }
        return (
          <Styled.Subnav
            key={group.id}
            data-cy={`subnav-${group.id}`}
            $themeColor={headerTheme}
            $headerIsVisible={headerIsVisible}
            $localeSwitchBannerIsVisible={localeSwitchBannerIsVisible}
            $isVisible={visibleSubnav === group.id}
            onMouseEnter={!!group.children ? () => showSubnav(group.id as number) : undefined}
            onMouseLeave={() => hideSubnav()}
            aria-label={
              t('global:navigation.highlights.subnavigation', { page: (group.page as LinkField).label }) as string
            }>
            <StyledGlobal.WrapperLarge>
              <Styled.Container>
                <HorizontalScrollContainer
                  fadeColor={headerThemes[headerTheme].subnav.background}
                  iconColor={headerThemes[headerTheme].subnav.text}>
                  {group.children.map((child, i) => {
                    if (isValidLinkField(child.page)) {
                      if (isStandaloneMode) {
                        return (
                          <NavigationHighlightLinkStandalone
                            key={`${i}-${child.page}`}
                            link={child.page}
                            headerTheme={headerTheme}
                            iconId={(child.icon as IconId) ?? 'TmhArrows'}
                            locale={locale}
                          />
                        );
                      } else {
                        return (
                          <NavigationHighlightLink
                            key={`${i}-${child.page}`}
                            link={child.page}
                            headerTheme={headerTheme}
                            // TODO how do we get the proper placeholder in here?
                            icon={(child.icon as IconSvgObject) ?? { children: '' }}
                          />
                        );
                      }
                    }
                  })}
                </HorizontalScrollContainer>
              </Styled.Container>
            </StyledGlobal.WrapperLarge>
          </Styled.Subnav>
        );
      })}
    </>
  );
};

export default SubNavigations;
